import React, { useRef, useEffect } from "react";
import { classNames } from "utils/utils";
import { useGame } from "context/gameContext";
import { useEventPick } from "context/eventPickContext";
import EventInfo from "components/eventInfo";
import { useLeague } from "context/leagueContext";
import { useRound } from "context/roundContext";
import { useEventPickData } from "hooks/useEventPickData";
import SkeletonLoader from "components/skeletonLoader";
import { IonItem, IonItemOptions, IonItemSliding } from "@ionic/react";
import { Md2ChevronLeft } from "react-icons-ng/md2";
import { useLongPress } from "ahooks";
import { useSlider } from "context/sliderContext";
import { PredictButton } from "components/predictButton";

const EventCard = ({ eventInfo, id }) => {
  const {
    state: { theme, settings },
  } = useGame();

  const {
    state: {
      tournamentInfo,
      leagueInfo,
      leagueUserData,
      isLoading: leagueLoading,
    },
  } = useLeague();

  const {
    state: { currentRound },
  } = useRound();

  const {
    state: {
      eventData: event,
      eventPick,
      isLoading: eventPickLoading,
      outcomes,
    },
  } = useEventPick();

  const {
    state: { sliders },
    dispatch,
  } = useSlider();

  useEventPickData(eventInfo);

  let sliderState = sliders[id] || { open: false, expanded: false };

  const itemSlideRef = useRef(null);

  useLongPress(
    (e) => {
      dispatch({
        type: "closeAllSliders",
        value: { id: id },
      });
    },
    itemSlideRef,
    {
      delay: 150,
      // moveThreshold: { y: 0 },
      onLongPressEnd: (e) => {
        if (e.cancelable) e.preventDefault();
        itemSlideRef.current.getSlidingRatio().then((res) => {
          let expandAndToggle = false;
          if (res > 0.5) {
            expandAndToggle = true;
          } else if (res < 0.49) {
            expandAndToggle = false;
          }

          dispatch({
            type: "toggleSlider",
            value: { id: id, open: expandAndToggle, expanded: expandAndToggle },
          });
          return;
        });
      },
      onClick: (e) => {
        if (e.cancelable) e.preventDefault();

        let ratio = itemSlideRef.current.getSlidingRatio();
        let expandAndToggle = false;
        if (ratio > 0.5 || sliderState?.open) {
          itemSlideRef.current.close();
          expandAndToggle = false;
        } else {
          itemSlideRef.current.open();
          expandAndToggle = true;
        }
        dispatch({
          type: "toggleSlider",
          value: { id: id, open: expandAndToggle, expanded: expandAndToggle },
        });
      },
    }
  );

  const onItemSwipe = (e) => {
    const ratio = e.detail.ratio;
    let toggle = false;
    if (ratio > 0.5) {
      toggle = true;
    } else if (ratio < 0.49) {
      toggle = false;
    }
    dispatch({
      type: "toggleSlider",
      value: { id: id, open: toggle },
    });
  };

  useEffect(() => {
    dispatch({
      type: "addSlider",
      value: id,
    });
  }, []);

  if (eventPickLoading) {
    return <SkeletonLoader type={"eventCard2"} amount={3} />;
  }

  let correctPointsWin =
    leagueInfo?.scoreRule == 6 || leagueInfo?.scoreRule == 8
      ? 1
      : leagueInfo?.scoreRule == 3 || leagueInfo?.scoreRule == 4
      ? leagueInfo?.fullEventCorrect
      : 0;
  let correctPointsPartial = leagueInfo?.partialEventCorrect;

  let eventStarted = event?.startTime?.seconds * 1000 < new Date();

  const eventData = {
    ...event,
    leagueId: leagueInfo?.leagueRef,
    seasonId: leagueInfo?.seasonRef,
    roundId: currentRound?.id,
    eventId: event?.id,
  };

  const showLogos =
    !settings?.copyrightMode ||
    tournamentInfo?.clubLogos ||
    leagueInfo?.clubLogos;

  const CSPredictButtons = () => {
    return (
      <div className="flex flex-col space-y-1 p-2">
        <div className="flex space-x-1">
          {outcomes &&
            Object.keys(outcomes?.home).map((key) => {
              return <PredictButton outcome={key} home={true} />;
            })}
        </div>
        <div className="flex space-x-1">
          {outcomes &&
            Object.keys(outcomes?.home).map((key) => {
              return <PredictButton outcome={key} home={false} />;
            })}
        </div>
      </div>
    );
  };

  const is1x2 = leagueInfo?.gameRule == 0 || leagueInfo?.gameRule == 4;
  const isCorrectScore = leagueInfo?.gameRule == 1 || leagueInfo?.gameRule == 3;
  const shouldDisplayRound =
    !leagueUserData?.gameOver ||
    leagueUserData?.passedRounds?.some((el) => el === currentRound?.id) ||
    leagueUserData?.exitRound == currentRound?.id;

  return (
    <div
      className={classNames(
        theme?.id === 0 && !event.settled ? "shadow-sm" : "",
        event.settled && isCorrectScore && shouldDisplayRound
          ? eventPick?.points == correctPointsWin
            ? "border-r-4 border-customGreen"
            : eventPick?.points && eventPick?.points == correctPointsPartial
            ? "border-r-4 border-yellow-500"
            : !event?.postponed && "border-r-4 border-red-500"
          : "",
        "flex rounded-md"
      )}
      key={id}
    >
      <EventInfo
        event={eventData}
        showLogos={showLogos}
        gameRule={leagueInfo?.gameRule}
        prediction={true}
      />

      {event?.postponed ? (
        <div className="flex rounded-r-md p-4 items-center bg-white text-xs text-center whitespace-nowrap text-gray-600">
          Postponed
        </div>
      ) : (
        <div
          className={classNames(
            event.settled || eventStarted
              ? "bg-white"
              : theme?.id === 0 && !event.settled
              ? "bg-gray-100"
              : "bg-white/60",
            "flex w-fit rounded-r-md  justify-center items-center" //p-2
          )}
        >
          {is1x2 ? (
            <div className="flex space-x-2 p-2">
              {outcomes &&
                Object.keys(outcomes).map((key) => {
                  return <PredictButton outcome={key} />;
                })}
            </div>
          ) : isCorrectScore && shouldDisplayRound ? (
            event.settled ? (
              <IonItemSliding
                className={classNames(
                  sliderState?.expanded ? "w-60 " : "w-40 ", //w-40
                  "h-[4.75rem]  noPaddingItem "
                )}
                mode="ios"
                key={id}
                ref={itemSlideRef}
                onIonDrag={(e) => {
                  onItemSwipe(e);
                }}
              >
                <IonItem
                  className=" shadow-2xl h-full bg-white noPaddingItem"
                  lines="none"
                  mode="ios"
                >
                  <div
                    className={classNames(
                      sliderState?.expanded
                        ? "animate-disappear_slide_fast"
                        : "opacity-100",
                      "flex items-center justify-end w-full h-[4.75rem]"
                    )}
                  >
                    {eventPick?.points > 0 && (
                      <div
                        className={classNames(
                          eventPick?.points == correctPointsWin
                            ? "bg-customGreen"
                            : eventPick?.points &&
                                eventPick?.points == correctPointsPartial &&
                                "bg-yellow-500",

                          "text-sm  py-1 w-fit p-2 items-center justify-center rounded-md" //
                        )}
                      >
                        +
                        {eventPick?.points +
                          (leagueInfo?.gameRule == 3 ||
                          leagueInfo?.scoreRule == 8
                            ? ""
                            : "p")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-center px-2 h-full ">
                    <Md2ChevronLeft
                      className={classNames(
                        sliderState?.open ? "rotate-180" : "",
                        "h-6 w-6"
                      )}
                    />
                  </div>
                </IonItem>

                <IonItemOptions side="end" className="noBorder ">
                  <CSPredictButtons />
                </IonItemOptions>
              </IonItemSliding>
            ) : (
              <CSPredictButtons />
            )
          ) : (
            <div className="flex rounded-r-md p-4"></div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventCard;
